import * as React from "react";
import {
  type ColumnDef,
  type ColumnFiltersState,
  type VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "../../../components/ui/button";
import { InstagramIcon } from "../../../assets/svgs/common/instagram.svg";
import { DataTableComponents } from "../../../components/tables/table.components";
import Spinner from "../../../components/spinner.components";
import { useNavigate, useParams } from "react-router-dom";
import type { ISocial } from "../../../types/models/social.models.types";
import { SocialPlatforms } from "../../../types/models/social.models.types";
import { useToast } from "../../../hooks/use-toast";
import millify from "millify";
import { handleHttpError } from "../../../utils/utils";
import { AddToGroup } from "../../../components/action-buttons/favorites/add-to-group-button.components";
import {
  type IFavoriteSocialMinimal,
  useGetFavoritesByIdAdminHttp,
} from "../../../hooks/http/admin/favorites/get-favorites-by-id.admin.http";
import { RemoveFromGroup } from "../../../components/action-buttons/favorites/remove-from-group-button.components";
import { DateTime } from "luxon";
import type { IPagination } from "../../../types/models/pagination.models.types";
import { Youtube } from "lucide-react";

type IProps = {
  tableType?: string;
  searchFetching?: boolean;
  search?: string;
  searchData?: IFavoriteSocialMinimal[];
};

export const FavoriteTableAdminPartial: React.FunctionComponent<IProps> = ({
  tableType,
  searchFetching,
  searchData,
}) => {
  const { favoriteId } = useParams();
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [data, setData] = React.useState<IFavoriteSocialMinimal[]>([
    ...(searchData ? searchData : []),
  ]);
  const [pageNo, setPageNo] = React.useState(1);
  const [paginationData, setPaginationData] = React.useState<IPagination>();
  const navigate = useNavigate();
  const {
    execute: getFavoritesByIdHttp,
    fetching: fetchingFavoritesbyId,
    cancel: cancelGetFavoritesByIdHttp,
  } = useGetFavoritesByIdAdminHttp();
  const { toast } = useToast();

  async function getSocials(
    existingSocials: IFavoriteSocialMinimal[],
    page: number
  ) {
    cancelGetFavoritesByIdHttp();
    if (!favoriteId) {
      return;
    }
    const response = await getFavoritesByIdHttp({
      body: null,
      pathParams: { id: favoriteId },
      queryParams: {
        page,
      },
    });

    if (!response.success) {
      return handleHttpError(response, toast);
    }

    const newSocials = response.data.favouriteSocial.socials.filter(
      (social) => {
        return (
          existingSocials.findIndex((i) => i.social.id === social.social.id) ===
          -1
        );
      }
    );

    setData([...existingSocials, ...newSocials]);
    setPaginationData(response.data.pagination);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    if (tableType === "search") {
      return;
    }
    // setData([]);
    getSocials(data, pageNo);
  }, [pageNo]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    setData(searchData ? searchData : []);
  }, [searchData]);

  function handleViewDetails(id: string) {
    navigate(`/admin/favorites/${favoriteId}/social/${id}`);
  }

  const RenderActionButtons = (socials: IFavoriteSocialMinimal) => {
    if (socials.createdAt && socials.createdAt) {
      return (
        <div className="flex justify-end items-center gap-4">
          <Button
            onClick={() => {
              handleViewDetails(socials.social.id);
            }}
            className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
          >
            View Details
          </Button>
          <RemoveFromGroup
            social={socials.social}
            removeById={() => {
              setData((prev) => {
                return prev.filter((i) => i.social.id !== socials.social.id);
              });
            }}
          />
        </div>
      );
    }

    if (!socials.createdAt && !socials.createdBy) {
      return (
        <div className="flex justify-end items-center gap-4">
          <Button
            onClick={() => {
              handleViewDetails(socials.social.id);
            }}
            className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
          >
            View Details
          </Button>
          <AddToGroup
            social={socials.social}
            removeById={() => {
              setData((prev) => {
                return prev.filter((i) => i.social.id !== socials.social.id);
              });
            }}
          />
        </div>
      );
    }
    // if (tableType === "search") {
    // 	return (
    // 		<div className="flex justify-end items-center gap-4">
    // 			<Button
    // 				onClick={() => {
    // 					handleViewDetails(socials.social.id);
    // 				}}
    // 				className="bg-tertiarybuttonbg p-2.5 border border-border rounded-md font-semibold tracking-wider"
    // 			>
    // 				View Details
    // 			</Button>
    // 			<AddToGroup social={socials.social} removeById={removeById} />
    // 		</div>
    // 	);
    // }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const columns = React.useMemo<ColumnDef<IFavoriteSocialMinimal>[]>(
    () => [
      {
        accessorKey: "social.handle",
        header: "User name",
        cell: ({ row }) => {
          const { social } = row.original;
          const { handle, platform, youtube } = social as ISocial;

          if (platform === SocialPlatforms.youtube) {
            const channelHandle = youtube?.channelHandle || handle;
            return (
              <div className="flex items-center gap-3 font-semibold tracking-wider">
                <div className="flex justify-center items-center border-2 rounded-full w-8 h-8 overflow-hidden">
                  {youtube?.profileImage ? (
                    <img
                      className="rounded-full w-8 h-8 cover"
                      src={youtube.profileImage}
                      alt={channelHandle ?? "YouTube channel"}
                    />
                  ) : null}
                </div>
                <a
                  href={`https://youtube.com/${channelHandle}/`}
                  target="_blank"
                  className="underline"
                  aria-label="YouTube channel link"
                  rel="noreferrer"
                >
                  {channelHandle}
                </a>
              </div>
            );
          }

          return (
            <div className="flex items-center gap-3 font-semibold tracking-wider">
              <div className="border-2 rounded-full w-8 h-8" />
              <a
                href={`https://instagram.com/${social.handle}/`}
                target="_blank"
                className="underline"
                aria-label="Instagram profile link"
                rel="noreferrer"
              >
                {social.handle}
              </a>
            </div>
          );
        },
      },
      {
        accessorKey: "social.engagementRate",
        header: "Engagement rate",
        cell: ({ row }) => {
          const { social } = row.original;
          const baseSocial = social as ISocial;
          const youtubeEngagement = baseSocial.youtube?.metrics?.engagementRate;
          const engagementToShow =
            baseSocial.platform === SocialPlatforms.youtube
              ? youtubeEngagement ?? baseSocial.engagementRate
              : baseSocial.engagementRate;

          return (
            <div className="flex justify-start items-center gap-2 w-fit font-semibold">
              {engagementToShow?.toFixed()}%
            </div>
          );
        },
      },
      {
        accessorKey: "social.followersCount",
        header: "Followers",
        cell: ({ row }) => {
          const { social } = row.original;
          const baseSocial = social as ISocial;
          const isYoutube = baseSocial.platform === SocialPlatforms.youtube;
          const youtubeSubs = baseSocial.youtube?.subscriberCount;
          const countToShow = isYoutube ? youtubeSubs : baseSocial.followersCount;

          return (
            <div className="flex items-center gap-2 font-semibold capitalize tracking-wider">
              {isYoutube ? (
                <Youtube className="w-5 h-5" />
              ) : (
                <InstagramIcon className="w-5 h-5" />
              )}{" "}
              {millify(countToShow ?? 0)}
            </div>
          );
        },
      },
      {
        accessorKey: "social.averageViews",
        header: "Average views",
        cell: ({ row }) => {
          const { social } = row.original;
          const baseSocial = social as ISocial;
          const isYoutube = baseSocial.platform === SocialPlatforms.youtube;
          const youtubeAverageViews = baseSocial.youtube?.metrics?.averageViews;
          const viewsToShow = isYoutube
            ? youtubeAverageViews ?? baseSocial.averageViews
            : baseSocial.averageViews;

          return (
            <div className="flex items-center gap-2 font-semibold capitalize tracking-wider">
              {millify(viewsToShow ?? 0)}
            </div>
          );
        },
      },
      {
        accessorKey: "createdBy",
        header: "Added by",
        cell: ({ row }) => {
          const { createdBy } = row.original;
          return (
            <div className="flex items-center gap-2 font-semibold text-secondary capitalize tracking-wider">
              {createdBy?.name ? createdBy.name : "-"}
            </div>
          );
        },
      },
      {
        accessorKey: "social.applicationStatus.timestamp",
        header: "Time added",
        cell: ({ row }) => {
          const { social } = row.original;
          return (
            <div className="flex items-center gap-2 font-semibold text-secondary capitalize tracking-wider">
              {DateTime.fromISO(
                social.applicationStatus.timestamp?.toString() || ""
              )
                .setZone("local")
                .toFormat("hh:mm a'\n'dd-MM-yy")}{" "}
            </div>
          );
        },
      },
      {
        accessorKey: "social.id",
        header: "",
        cell: ({ row: social }) => RenderActionButtons(social.original),
      },
    ],
    []
  );
  const table = useReactTable({
    data,
    columns,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
  });

  console.log(data);
  return (
    <div className="">
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <div
        id="getSocials"
        onClick={() => {
          getSocials([], 1);
        }}
        className="hidden"
      />
      {(data.length <= 0 && fetchingFavoritesbyId) || searchFetching ? (
        <div className="flex justify-center items-center pt-20 w-full">
          <Spinner />
        </div>
      ) : (
        <DataTableComponents
          table={table as ReturnType<typeof useReactTable>}
          columns={columns as ColumnDef<ISocial>[]}
          paginationData={tableType === "search" ? undefined : paginationData}
          setPageNo={() => {
            setPageNo((prev) => {
              const nextPage = prev + 1;
              return nextPage;
            });
          }}
          loading={fetchingFavoritesbyId}
          tableType="favorites"
        />
      )}
    </div>
  );
};
